import React, { useState, useMemo } from 'react';
import {
    TextField,
    InputAdornment,
    IconButton,
    Box,
    Typography,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface PasswordFieldProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    required?: boolean;
    showStrengthIndicator?: boolean;
    showRequirements?: boolean;
    helperText?: string;
    fullWidth?: boolean;
    error?: boolean;
}

interface PasswordRequirements {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasSpecialChar: boolean;
}

type PasswordStrength = 'weak' | 'medium' | 'strong';

// Mesmo regex do backend
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=\[\]{};':""\\|,.<>\/?]).+$/;

const PasswordField: React.FC<PasswordFieldProps> = ({
    value,
    onChange,
    label = 'Senha',
    required = false,
    showStrengthIndicator = true,
    showRequirements = true,
    helperText,
    fullWidth = true,
    error = false
}) => {
    const [showPassword, setShowPassword] = useState(false);

    const validatePasswordRequirements = (password: string): PasswordRequirements => {
        return {
            minLength: password.length >= 6,
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':""\\|,.<>\/?]/.test(password)
        };
    };

    const calculatePasswordStrength = (password: string): PasswordStrength => {
        if (password.length === 0) return 'weak';

        const requirements = validatePasswordRequirements(password);
        const score = Object.values(requirements).filter(Boolean).length;

        if (score <= 1) return 'weak';
        if (score <= 3) return 'medium';
        return 'strong';
    };

    const requirements = useMemo(() => validatePasswordRequirements(value), [value]);
    const strength = useMemo(() => calculatePasswordStrength(value), [value]);

    const strengthConfig = {
        weak: { label: 'Fraca', color: 'error' as const, percentage: 25 },
        medium: { label: 'Média', color: 'warning' as const, percentage: 60 },
        strong: { label: 'Forte', color: 'success' as const, percentage: 100 }
    };

    const config = strengthConfig[strength];

    const handleTogglePassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Box>
            <TextField
                fullWidth={fullWidth}
                type={showPassword ? 'text' : 'password'}
                label={label}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                helperText={helperText}
                error={error}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                            >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />

            {/* Indicador de força */}
            {showStrengthIndicator && value.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" color={`${config.color}.main`}>
                            Força: {config.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {config.percentage}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={config.percentage}
                        color={config.color}
                        sx={{ height: 6, borderRadius: 3 }}
                    />
                </Box>
            )}

            {/* Lista de requisitos */}
            {showRequirements && value.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Requisitos:
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                        <ListItem sx={{ py: 0, px: 0, minHeight: 28 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                {requirements.minLength ?
                                    <CheckCircleIcon color="success" fontSize="small" /> :
                                    <CancelIcon color="error" fontSize="small" />
                                }
                            </ListItemIcon>
                            <ListItemText
                                primary="Mínimo 6 caracteres"
                                primaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0, px: 0, minHeight: 28 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                {requirements.hasUppercase ?
                                    <CheckCircleIcon color="success" fontSize="small" /> :
                                    <CancelIcon color="error" fontSize="small" />
                                }
                            </ListItemIcon>
                            <ListItemText
                                primary="Uma letra maiúscula"
                                primaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0, px: 0, minHeight: 28 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                {requirements.hasLowercase ?
                                    <CheckCircleIcon color="success" fontSize="small" /> :
                                    <CancelIcon color="error" fontSize="small" />
                                }
                            </ListItemIcon>
                            <ListItemText
                                primary="Uma letra minúscula"
                                primaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0, px: 0, minHeight: 28 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                {requirements.hasSpecialChar ?
                                    <CheckCircleIcon color="success" fontSize="small" /> :
                                    <CancelIcon color="error" fontSize="small" />
                                }
                            </ListItemIcon>
                            <ListItemText
                                primary="Um caractere especial (!@#$%...)"
                                primaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                    </List>
                </Box>
            )}
        </Box>
    );
};

export default PasswordField;
export { PASSWORD_REGEX };
export type { PasswordRequirements, PasswordStrength };
